<template>
  <div>
    <v-btn id="new_secret_back_btn" icon @click="backPage">
      <v-icon>chevron_left</v-icon>
    </v-btn>

    <card-toolbar :title="$tc('new_secret')" />

    <v-row>
      <v-col
        cols="12"
        md="6"
      >
        <v-text-field
          id="new_secret_name"
          class="mb-3"
          :label="$tc('name')"
          :hint="$tc('key_name_hint')"
          v-model="name"
          :error-messages="nameErrors"
          counter
          @input="$v.name.$touch()"
          @blur="$v.name.$touch()"
        />

        <v-autocomplete
          id="new_secret_secLevelOption"
          class="mb-3"
          v-model="secLevelOption"
          :items="secLevelOptions"
          item-text="name"
          item-value="value"
          auto-select-first
          :label="$t('select_secret_sec_level')"
          :error-messages="secLevelOptionErrors"
          @input="$v.secLevelOption.$touch()"
          @blur="$v.secLevelOption.$touch()"
        />

        <span
          class="subtitle-1 font-weight-regular"
        >
          {{ $tc('select_secret_type') }}
        </span>

        <v-row no-gutters>
          <v-checkbox
            id="new_secret_smart_card"
            class="mr-2"
            color="blue"
            :label="$tc('select_type_m_of_n')"
            v-model="smartCard"
          />

          <v-checkbox
            id="new_secret_oath"
            class="mr-2"
            color="blue"
            :label="$tc('type_auth_oath')"
            v-model="oath"
            @click="getOath"
          />

          <v-checkbox
            id="new_secret_x509"
            class="mr-2"
            color="blue"
            :label="$tc('type_auth_X509')"
            v-model="x509"
            @click="getX509"
          />
        </v-row>
      </v-col>

      <v-container>
        <v-row justify="end" class="mb-4 mt-2">
          <v-btn
            id="select_secret_save"
            color="blue"
            :disabled="$v.$invalid"
            :dark="!$v.$invalid"
            @click="submit"
          >
            <div v-if="!loading">
              {{ $t("save") }}
            </div>
            <div v-else>
              <v-progress-circular
                indeterminate
              />
            </div>
          </v-btn>
        </v-row>
      </v-container>
    </v-row>
  </div>
</template>

<script>
import CardToolbar from '@/components/CardToolbar'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'

export default {
  components: {
    CardToolbar
  },
  data () {
    return {
      name: null,
      secLevelOptions: [
        {
          name: this.$tc('sec_level_64b'),
          value: 1
        },
        {
          name: this.$tc('sec_level_96b'),
          value: 2
        },
        {
          name: this.$tc('sec_level_112b'),
          value: 3
        },
        {
          name: this.$tc('sec_level_128b'),
          value: 4
        },
        {
          name: this.$tc('sec_level_160b'),
          value: 5
        }
      ],
      secLevelOption: null,
      loading: false,
      smartCard: false,
      openTheGlass: false,
      oath: false,
      x509: false
    }
  },
  computed: {
    inputPolicy () {
      return this.$store.state.secPolicy
    },
    nameErrors () {
      const errors = []
      if (!this.$v.name.$dirty) return errors
      !this.$v.name.required && errors.push(this.$t('$validation.required'))
      !this.$v.name.minLength &&
        errors.push(
          this.$t('$validation.min_length', {
            num: this.$v.name.$params.minLength.min
          })
        )
      !this.$v.name.maxLength &&
        errors.push(
          this.$t('$validation.max_length', {
            num: this.$v.name.$params.maxLength.max
          })
        )
      !this.$v.name.keyExists && errors.push('Key exists')
      !this.$v.name.validChars &&
        errors.push(this.$t('$validation.invalid_key_name'))
      return errors
    },
    typeOptionErrors () {
      const errors = []
      if (!this.$v.typeOption.$dirty) return errors
      !this.$v.typeOption.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    secLevelOptionErrors () {
      const errors = []
      if (!this.$v.secLevelOption.$dirty) return errors
      !this.$v.secLevelOption.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    typeOption () {
      const MSK_TYPE_SCRD = 1
      const MSK_TYPE_2FA = 2
      const MSK_TYPE_CRT = 4

      const selectedTypes = []

      if (this.smartCard) {
        selectedTypes.push(MSK_TYPE_SCRD)
      }

      if (this.oath) {
        selectedTypes.push(MSK_TYPE_2FA)
      }

      if (this.x509) {
        selectedTypes.push(MSK_TYPE_CRT)
      }

      return selectedTypes.reduce((acc, cur) => acc | cur, 0)
    }
  },
  validations () {
    const validation = {}

    const keyExists = value => {
      if (!value) return true
      const keys = this.$store.state.hsm.listObjects
      const find = keys.find(key => key.id === value)
      return find === undefined
    }

    const validChars = value => {
      if (!value) return true
      const numbeOfMatches = (value.match(/^([A-Za-z0-9]+\/)?[A-Za-z0-9_]+$/g))
      if (!numbeOfMatches) return false
      return numbeOfMatches.length === 1
    }

    validation.name = {
      required,
      minLength: minLength(this.inputPolicy.key_name_min_len),
      maxLength: maxLength(this.inputPolicy.key_name_max_len),
      keyExists,
      validChars
    }

    validation.typeOption = {
      required
    }

    validation.secLevelOption = {
      required
    }

    return validation
  },
  methods: {
    backPage () {
      const safeKeepingTab = 0

      this.$router.push(
        {
          name: 'hsm-tools',
          params: {
            tabOption: safeKeepingTab
          }
        }
      )
    },
    getOath () {
      if (this.oath) {
        this.x509 = false
      }
    },
    getX509 () {
      if (this.x509) {
        this.oath = false
      }
    },
    submit () {
      this.loading = true

      this.$store.dispatch('hsm/skeep_new_secret', {
        body: {
          obj: this.name,
          type: this.typeOption,
          sec_level: this.secLevelOption
        }
      })
        .then(() => {
          this.$notify.success({})
          this.loading = false
          this.backPage()
        })
        .catch((err) => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }

          this.loading = false
          this.backPage()
        })
    }
  }
}
</script>
